/**
 * SBSTriggerEffect — a thin wrapper component that calls useSBSTrigger so
 * the hook (and its side effects) only run when the component is mounted.
 *
 * Render this behind a feature flag check so that non-SBS users never pay
 * the cost of the hook's fetch / event listeners.
 *
 * This component renders nothing — it only exists to own the hook lifecycle
 * and expose a single `interceptSubmit` function to the parent via ref.
 */

import { type MutableRefObject } from 'react';

import { useSBSTrigger } from '@atlassian/rovo-chat-side-by-side-evaluation/common/utils/useSBSTrigger';

type SBSTriggerParams = Parameters<ReturnType<typeof useSBSTrigger>['triggerSbsEvaluation']>[0];

/**
 * Attempts to intercept a submit for SBS evaluation.
 * Returns `true` if the query was held (caller should NOT submit).
 * Returns `false` if the query was not held (caller should submit normally).
 */
export type InterceptSubmitFn = (params: SBSTriggerParams, doSendMessage: () => void) => boolean;

interface SBSTriggerEffectProps {
	productKey: string;
	experienceId: string;
	siteId: string;
	/** Ref that will be set to the interceptSubmit function while this component is mounted. */
	interceptSubmitRef: MutableRefObject<InterceptSubmitFn | null>;
}

export function SBSTriggerEffect({
	productKey,
	experienceId,
	siteId,
	interceptSubmitRef,
}: SBSTriggerEffectProps): null {
	const { triggerSbsEvaluation, setQueryReleaseCallback } = useSBSTrigger({
		productKey,
		experienceId,
		siteId,
	});

	// Keep the ref in sync with the latest hook values.
	interceptSubmitRef.current = (params, doSendMessage) => {
		const { shouldHoldQuery } = triggerSbsEvaluation(params);
		if (shouldHoldQuery) {
			setQueryReleaseCallback(doSendMessage);
			return true;
		}
		return false;
	};

	return null;
}
